import React, { useEffect } from 'react';
import { Button } from 'antd';
import { ArrowLeftOutlined, FileTextOutlined } from '@ant-design/icons';
import logoHandBtn from '../assets/logo_hand_button.png';

interface TermsOfServiceViewProps {
  variant?: 'public' | 'app';
  onBack?: () => void;
}

const SECTIONS = [
  {
    title: '1. Acceptance of Terms',
    body: 'By accessing or using Praxys.ai, you agree to these Terms of Service. If you are using Praxys.ai on behalf of an organization, you represent that you have authority to bind that organization to these terms.',
  },
  {
    title: '2. Service Description',
    body: 'Praxys.ai provides AI-assisted task execution, knowledge management, connector integrations, and agent workflows. Features may change over time as the product evolves.',
  },
  {
    title: '3. Accounts and Security',
    body: 'You are responsible for maintaining access to your account, protecting credentials, and all activity performed through your account. Notify us if you believe your account or connected services have been compromised.',
  },
  {
    title: '4. Acceptable Use',
    body: 'You may not use Praxys.ai to violate laws, infringe rights, compromise systems, distribute malware, scrape or exfiltrate data without authorization, or interfere with the service or other users.',
  },
  {
    title: '5. User Content and Connected Data',
    body: 'You retain ownership of prompts, documents, files, connector data, and other content you provide. You grant Praxys.ai permission to process that content as needed to provide and improve the service.',
  },
  {
    title: '6. AI Outputs',
    body: 'AI-generated outputs may be incomplete, inaccurate, or unsuitable for a particular purpose. You are responsible for reviewing outputs before relying on them, especially for legal, financial, medical, security, or operational decisions.',
  },
  {
    title: '7. Third-Party Services',
    body: 'Connectors such as Google Drive, Google Calendar, Microsoft Teams, Slack, and GitHub are governed by their own terms. Praxys.ai is not responsible for third-party services or changes to their APIs, availability, or policies.',
  },
  {
    title: '8. Billing and Plans',
    body: 'If paid plans are offered, fees, limits, renewals, cancellations, and refunds will be described at checkout or in the applicable order terms. You are responsible for taxes and charges associated with your plan.',
  },
  {
    title: '9. Suspension and Termination',
    body: 'We may suspend or terminate access if we reasonably believe these terms were violated, the service is being abused, or continued access creates risk to Praxys.ai, users, or third parties.',
  },
  {
    title: '10. Disclaimers',
    body: 'Praxys.ai is provided as is and as available. We disclaim warranties to the maximum extent permitted by law, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement.',
  },
  {
    title: '11. Limitation of Liability',
    body: 'To the maximum extent permitted by law, Praxys.ai and its operators will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or for lost profits, data, goodwill, or business interruption.',
  },
  {
    title: '12. Changes to These Terms',
    body: 'We may update these terms from time to time. Continued use of Praxys.ai after changes become effective means you accept the updated terms.',
  },
];

const TermsOfServiceView: React.FC<TermsOfServiceViewProps> = ({ variant = 'app', onBack }) => {
  useEffect(() => {
    document.title = 'Terms of Service | Praxys.ai — makes better';
  }, []);

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    window.history.replaceState({}, '', '/app');
    window.location.assign('/app');
  };

  const content = (
    <div className="praxys-view-panel praxys-terms-view">
      {variant === 'app' && (
        <Button
          icon={<ArrowLeftOutlined />}
          className="praxys-agent-pill-btn praxys-agent-pill-btn--accent praxys-legal-back-btn"
          onClick={handleBack}
          style={{ alignSelf: 'flex-start', width: 'auto', marginBottom: 16 }}
        >
          Back
        </Button>
      )}
      <div className="praxys-view-header">
        <FileTextOutlined className="praxys-view-icon" />
        <h2 className="praxys-view-title">Terms of Service</h2>
        <span className="praxys-view-subtitle">Last updated June 23, 2026</span>
      </div>

      <div className="praxys-terms-card">
        <p className="praxys-terms-intro">
          These basic Terms of Service are provided for Praxys.ai users. They are a practical starting point and are not a substitute for legal advice.
        </p>

        <div className="praxys-terms-section-list">
          {SECTIONS.map(section => (
            <section key={section.title} className="praxys-terms-section">
              <h3>{section.title}</h3>
              <p>{section.body}</p>
            </section>
          ))}
        </div>

        <section className="praxys-terms-contact">
          <h3>Contact</h3>
          <p>
            Questions about these terms can be sent to <a href="mailto:support@praxys.ai">support@praxys.ai</a>.
          </p>
        </section>
      </div>
    </div>
  );

  if (variant === 'public') {
    return (
      <main className="praxys-public-home praxys-public-legal-page">
        <nav className="praxys-public-nav" aria-label="Public navigation">
          <a href="/" className="praxys-public-brand">
            <img src={logoHandBtn} alt="Praxys.ai" />
            <span>Praxys.ai</span>
            <span className="praxys-public-brand-tagline">makes better</span>
          </a>
          <div className="praxys-public-nav-links">
            <a href="/privacy">Privacy Policy</a>
            <a href="/terms">Terms of Service</a>
            <a href="/login" className="praxys-public-login-link">Login</a>
          </div>
        </nav>
        {content}
      </main>
    );
  }

  return content;
};

export default TermsOfServiceView;
